import { useEffect, useMemo, useState } from 'react';
import {
  ARROW,
  type AssignmentDto,
  dayEndSlot,
  dayStartSlot,
  formatDayShort,
  formatMonthYear,
  monthGridSlotRange,
  monthSlotRange,
  todayDayIndex,
} from '@betreuung/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAssignmentRange } from '@/api/queries';
import { FloatingPill } from '@/components/shared/FloatingPill';
import { SegmentedControl } from '@/components/shared/SegmentedControl';
import { Button } from '@/components/ui/button';
import { useAppData } from '@/app/appData';
import { useNavigation } from '@/app/navigation';
import { TabHeader } from '@/app/TabHeader';
import { useIsDesktop, useIsTouch } from '@/app/useMediaQuery';
import { useAssignmentFlows } from '@/features/assignment/useAssignmentFlows';
import { TimelineSheet } from '@/features/timeline/TimelineSheet';
import { de } from '@/i18n/de';
import { MonthGrid } from './MonthGrid';
import { MonthLegend } from './MonthLegend';
import { type SelectionMode, useMonthSelection } from './useMonthSelection';

const NO_ASSIGNMENTS: readonly AssignmentDto[] = [];

/**
 * Month overview and the entry point into a single day.
 *
 * A tap does exactly one thing, and which one depends on the selection mode: in
 * "Einzeltag" it opens the day's timeline, in "Zeitraum" it sets the start and
 * then the end of a whole-day range for the assignment modal. The inline detail
 * row is reached separately, through the control in the cell.
 */
export function MonthTab() {
  const { monthCursor, stepMonth, goToToday } = useNavigation();
  const { persons, now, canWrite } = useAppData();
  const { openCreate } = useAssignmentFlows();
  const isDesktop = useIsDesktop();
  const isTouch = useIsTouch();
  const [timelineDay, setTimelineDay] = useState<number | null>(null);

  const { year, month } = monthCursor;
  const {
    selectionMode: storedMode,
    setSelectionMode,
    selection,
    tap,
    reset,
  } = useMonthSelection(year, month);

  // The mode is restored from localStorage regardless of who is signed in, so a
  // view-only account on a device a parent used would otherwise land in range
  // mode — where the switch is hidden, the pill that consumes a selection is
  // hidden, and tapping a day therefore does nothing at all instead of opening
  // its timeline. Clamping and hiding are one change, not two.
  const selectionMode = canWrite ? storedMode : 'single';

  // Without this the sheet stays open across a month change, showing a day that
  // is no longer in the loaded window.
  useEffect(() => setTimelineDay(null), [year, month]);

  const gridRange = useMemo(() => monthGridSlotRange(year, month), [year, month]);
  const visibleMonth = useMemo(() => monthSlotRange(year, month), [year, month]);
  const assignmentsQuery = useAssignmentRange(gridRange);
  const assignments = assignmentsQuery.data ?? NO_ASSIGNMENTS;

  const legendProps = {
    assignments,
    persons,
    fromSlot: visibleMonth.fromSlot,
    toSlot: visibleMonth.toSlot,
  };

  const actions = (
    <>
      <Button
        variant="ghost"
        size={isDesktop ? 'iconSm' : 'icon'}
        className="hit-44"
        aria-label={de.month.previous}
        onClick={() => stepMonth(-1)}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size={isDesktop ? 'iconSm' : 'icon'}
        className="hit-44"
        aria-label={de.month.next}
        onClick={() => stepMonth(1)}
      >
        <ChevronRight className="size-4" />
      </Button>
      {isDesktop ? (
        <>
          <Button variant="outline" size="compact" className="py-[5px]" onClick={goToToday}>
            {de.month.today}
          </Button>
          <MonthLegend {...legendProps} className="px-0 pt-0 pl-3" />
        </>
      ) : null}
    </>
  );

  return (
    <div className="pb-5 nav:pt-1 nav:pb-6">
      {/* The month title is one step smaller than a screen title on phones, and
          the header owns the heading element. */}
      <TabHeader
        title={formatMonthYear(year, month)}
        actions={actions}
        showSettings={!isDesktop}
        className="[&_h1]:text-month nav:[&_h1]:text-screen"
      />

      <div className="flex items-center justify-between gap-3 px-4 pt-0.5 pb-2">
        <p className="min-w-0 text-hint text-fg-hint">
          {selectionMode === 'single'
            ? isTouch
              ? de.month.hintSingleTouch
              : de.month.hintSinglePointer
            : isTouch
              ? de.month.hintRangeTouch
              : de.month.hintRangePointer}
        </p>
        {canWrite ? (
          <SegmentedControl<SelectionMode>
            className="shrink-0"
            aria-label={de.month.selectionMode}
            value={selectionMode}
            onChange={setSelectionMode}
            options={[
              { value: 'single', label: de.month.modeSingle },
              { value: 'range', label: de.month.modeRange },
            ]}
          />
        ) : null}
      </div>

      <div className="nav:px-4">
        <MonthGrid
          year={year}
          month={month}
          assignments={assignments}
          persons={persons}
          bandHeight={isDesktop ? 10 : 8}
          todayDay={todayDayIndex(now)}
          selection={selection}
          onDayTap={(day) => {
            if (selectionMode === 'single') setTimelineDay(day);
            else tap(day);
          }}
        />
      </div>

      {isDesktop ? null : <MonthLegend {...legendProps} />}

      {selection.mode === 'range' ? (
        <FloatingPill
          className="mx-4 mt-3"
          label={`${formatDayShort(selection.startDay)} ${ARROW} ${formatDayShort(selection.endDay)}`}
          actionLabel={de.month.assign}
          onAction={() => {
            openCreate({
              startSlot: dayStartSlot(selection.startDay),
              endSlot: dayEndSlot(selection.endDay),
            });
            reset();
          }}
          onDismiss={reset}
          dismissLabel={de.month.clearSelection}
        />
      ) : null}

      {timelineDay !== null ? (
        <TimelineSheet
          dayIndex={timelineDay}
          assignments={assignments}
          onClose={() => setTimelineDay(null)}
        />
      ) : null}
    </div>
  );
}
